import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, Loader2 } from "lucide-react";
import { api, fmtErr, input, monthLabel, monthOptions, prevMonth } from "./shared";
import { curMonth } from "./attendanceUi";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const Chip = ({ cls, children, title }) => <span className={`block truncate text-[10px] leading-4 px-1 border ${cls}`} title={title}>{children}</span>;

/** Month grid: Sundays, company holidays, WFH days and approved leave per staff — every day chat is blocked, at a glance. */
export const LeaveCalendar = () => {
  const [month, setMonth] = useState(curMonth());
  const [d, setD] = useState(null);
  useEffect(() => { let alive = true; setD(null); api.get("/biometric/calendar", { params: { month } }).then((r) => alive && setD(r.data)).catch((e) => toast.error(fmtErr(e))); return () => { alive = false; }; }, [month]);
  const today = new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
  const lead = d ? (new Date(d.days[0].day + "T00:00:00").getDay() + 6) % 7 : 0;
  const totals = d ? { holidays: d.days.filter((x) => x.holiday).length, wfh: d.days.filter((x) => x.wfh).length, leave: d.days.reduce((n, x) => n + x.leaves.length, 0) } : null;
  return (
    <div className="border border-white/10 bg-white/[0.02]" data-testid="leave-calendar">
      <div className="px-3 py-2.5 flex items-center gap-3 flex-wrap border-b border-white/10">
        <CalendarRange size={14} className="text-nx-goldlight" /><span className="text-sm text-white">Leave & blocked-day calendar</span>
        <span className="text-[11px] text-slate-500 hidden sm:inline">Sundays · holidays · WFH · approved leave — chat is closed on these days unless the CEO allows someone</span>
        <span className="flex-1" />
        {totals && <span className="text-[11px] text-slate-400" data-testid="calendar-totals">Holidays {totals.holidays} · WFH {totals.wfh} · Leave days {totals.leave}</span>}
        <select value={month} onChange={(e) => setMonth(e.target.value)} className={`${input} !w-auto !py-1.5`} data-testid="calendar-month">
          {[prevMonth(-1), ...monthOptions(6)].map((m) => <option key={m} value={m} className="text-neutral-900">{monthLabel(m)}</option>)}
        </select>
      </div>
      {!d ? <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-slate-500" size={18} /></div> : (
        <div className="p-3">
          <div className="grid grid-cols-7 gap-px text-[10px] uppercase tracking-wider text-slate-500 mb-1">{DOW.map((x) => <div key={x} className="px-1">{x}</div>)}</div>
          <div className="grid grid-cols-7 gap-px bg-white/10 border border-white/10" data-testid="calendar-grid">
            {Array.from({ length: lead }).map((_, i) => <div key={`e${i}`} className="bg-nx-navy/60 min-h-[76px]" />)}
            {d.days.map((x) => {
              const blocked = x.weekly_off || x.holiday || x.wfh;
              return (
                <div key={x.day} className={`min-h-[76px] p-1 space-y-0.5 ${blocked ? "bg-white/[0.04]" : "bg-nx-navy"} ${x.day === today ? "ring-1 ring-inset ring-nx-gold/60" : ""}`} data-testid={`cal-${x.day}`}>
                  <div className={`text-xs ${x.weekly_off ? "text-slate-500" : "text-slate-200"}`}>{+x.day.slice(8)}</div>
                  {x.weekly_off && <Chip cls="text-slate-400 border-white/15">Weekly off</Chip>}
                  {x.holiday && <Chip cls="text-red-200 border-red-400/40 bg-red-500/10" title={x.holiday}>{x.holiday}</Chip>}
                  {x.wfh && <Chip cls="text-blue-200 border-blue-400/40 bg-blue-500/10" title={x.wfh_note}>WFH</Chip>}
                  {x.leaves.slice(0, 3).map((l, i) => <Chip key={i} cls="text-sky-200 border-sky-400/40 bg-sky-500/10" title={`${l.name} — ${l.type}`}>{l.name.split(" ")[0]} · leave</Chip>)}
                  {x.leaves.length > 3 && <Chip cls="text-sky-300 border-transparent">+{x.leaves.length - 3} more</Chip>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
